import { Dialog, Select, SelectOption } from '@cimplico/components';
import { useState } from 'react';
import { toastNotification } from '../../components/toast/Toast';
import { useUpdateUserFirmRoles } from '../../queries/use-update-user-firm-roles';
import { User, UserRoleEnum } from '../../types/user';

type UserRoleEditDialogProps = {
    users: User[];
    isOpen: boolean;
    onClose: () => void;
    onUpdate: () => void;
};

function UserRoleEditDialog({ users, isOpen, onClose, onUpdate }: UserRoleEditDialogProps): JSX.Element {
    const [selectedRole, setSelectedRole] = useState<UserRoleEnum | undefined>(undefined);
    const [errorMessage, setErrorMessage] = useState<string>();
    const updateUserFirmRoleQuery = useUpdateUserFirmRoles();

    function handleRoleChange(options: SelectOption[]): void {
        if (options.length === 0) {
            setSelectedRole(undefined);
        } else {
            setSelectedRole(options[0].value as UserRoleEnum);
            setErrorMessage(undefined);
        }
    }

    function handleClose(): void {
        setSelectedRole(undefined);
        onClose();
    }

    function handleSubmit(): void {
        if (!selectedRole) {
            setErrorMessage('A role must be selected.');
        }

        // Filter out users with roles changed
        const filteredUsers = users.filter((u) => u.role !== selectedRole);

        if (filteredUsers.length === 0) {
            handleClose();
            return;
        }

        updateUserFirmRoleQuery.mutate(
            {
                ids: filteredUsers.map((u) => u.id),
                fields: { role: selectedRole },
            },
            {
                onSuccess: () => {
                    setSelectedRole(undefined);
                    onUpdate();
                    toastNotification(
                        'success',
                        `${filteredUsers.length} user firm ${filteredUsers.length === 1 ? 'role' : 'roles'} updated!`
                    );
                },
            }
        );
    }

    return (
        <Dialog
            title="Edit Role"
            description="Select the role you wish to assign to the selected users."
            type="info"
            isOpen={isOpen}
            primaryAction="Edit Role"
            secondaryAction="Cancel"
            onClose={onClose}
            onPrimaryActionClick={handleSubmit}
            onSecondaryActionClick={handleClose}
        >
            <div className="pr-6 pb-4">
                <Select
                    placeholder="Select a role..."
                    errorMessage={errorMessage}
                    onChange={(options): void => handleRoleChange(options)}
                    options={[
                        {
                            value: UserRoleEnum.MANAGER,
                            label: 'Manager',
                            isSelected: selectedRole === UserRoleEnum.MANAGER,
                        },
                        {
                            value: UserRoleEnum.USER,
                            label: 'User',
                            isSelected: selectedRole === UserRoleEnum.USER,
                        },
                    ]}
                />
            </div>
        </Dialog>
    );
}
export default UserRoleEditDialog;
